import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LandingLayout } from "../../features/landing/components/LandingLayout";
import { HeroSection } from "../../features/landing/components/HeroSection";
import { FeaturesGrid } from "../../features/landing/components/FeaturesGrid";
import { HowItWorks } from "../../features/landing/components/HowItWorks";
import { PricingSection } from "../../features/landing/components/PricingSection";
import { FooterCta } from "../../features/landing/components/FooterCta";
import { useSeoHead } from "../../shared/seo/useSeoHead";
import { landingSeo } from "../../shared/seo/landingSeo";
import type { AppLanguage } from "../../shared/i18n/localeRoutes";

interface LandingPageProps {
  locale: AppLanguage;
}

export function LandingPage({ locale }: LandingPageProps) {
  const { i18n } = useTranslation();

  useSeoHead(landingSeo(locale));

  // i18n.ts sets the language on first load, but client-side navigation between
  // / and /de does not re-run it — this keeps the two in step.
  //
  // It also has a second, undocumented-until-now role: React fires child
  // effects before parent effects, so on first mount this effect runs *after*
  // LanguageSelector's, and re-asserts `i18n.language = locale` over whatever
  // a stale-localStorage LanguageSelector mount just set. Removing this
  // effect would silently reopen that bug even though it looks unrelated to
  // it — see the "#language selector does not override the URL locale" test
  // in src/features/landingRoutes.test.tsx.
  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  return (
    <LandingLayout>
      <HeroSection />
      <FeaturesGrid />
      <HowItWorks />
      <PricingSection />
      <FooterCta />
    </LandingLayout>
  );
}

export default LandingPage;
